import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Global UX chrome state:
 *  - command palette open/close
 *  - shortcuts cheatsheet open/close
 *  - notifications panel open/close
 *  - per-page "primary new action" registration (Cmd+N)
 *  - per-page filter-search focus target
 *  - per-page "clear filters" function (used by emptyState)
 *  - dynamic breadcrumb entity label
 */
const UXChromeContext = createContext(null);

export function UXChromeProvider({ children }) {
    const [commandOpen, setCommandOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [pageAction, setPageAction] = useState(null); // {label, run}
    const [entityCrumb, setEntityCrumbState] = useState(null);
    const filterFocusRef = useRef(null); // function to focus filter input

    const setEntityCrumb = useCallback((label) => setEntityCrumbState(label || null), []);

    const value = useMemo(() => ({
        commandOpen, setCommandOpen,
        shortcutsOpen, setShortcutsOpen,
        notificationsOpen, setNotificationsOpen,
        pageAction, setPageAction,
        entityCrumb, setEntityCrumb,
        filterFocusRef,
    }), [commandOpen, shortcutsOpen, notificationsOpen, pageAction, entityCrumb, setEntityCrumb]);

    return <UXChromeContext.Provider value={value}>{children}</UXChromeContext.Provider>;
}

export function useUXChrome() {
    const ctx = useContext(UXChromeContext);
    if (!ctx) throw new Error("useUXChrome must be used inside UXChromeProvider");
    return ctx;
}

/**
 * Per-page "primary new action" registration. Cmd/Ctrl+N triggers it.
 * Use:  usePageAction({ label: "Add Employee", run: () => setOpen(true) }, [open])
 */
export function usePageAction(action, deps = []) {
    const { setPageAction } = useUXChrome();
    useEffect(() => {
        setPageAction(action);
        return () => setPageAction(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/**
 * Used by detail pages to override the last breadcrumb segment with a real entity name.
 */
export function useEntityCrumb(label) {
    const { entityCrumb, setEntityCrumb } = useUXChrome();
    useEffect(() => {
        if (label !== undefined) {
            setEntityCrumb(label);
            return () => setEntityCrumb(null);
        }
    }, [label, setEntityCrumb]);
    return { entityCrumb, setEntityCrumb };
}

/**
 * Register a focus handler that "/" can call to focus the page's filter input.
 */
export function useFilterFocus(fn) {
    const { filterFocusRef } = useUXChrome();
    useEffect(() => {
        filterFocusRef.current = fn;
        return () => { if (filterFocusRef.current === fn) filterFocusRef.current = null; };
    }, [fn, filterFocusRef]);
}

/**
 * Global keybinding manager: Cmd+K, Cmd+N, "/", "?", and the "G then X" sequence.
 */
export function useGlobalShortcuts() {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        setCommandOpen, setShortcutsOpen, setNotificationsOpen,
        pageAction, filterFocusRef,
    } = useUXChrome();

    const gPressed = useRef(false);
    const gTimer = useRef(null);

    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target?.tagName || "").toUpperCase();
            const isTyping = tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;
            const meta = e.metaKey || e.ctrlKey;

            // Cmd/Ctrl+K — command palette
            if (meta && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCommandOpen((o) => !o);
                return;
            }
            // Cmd/Ctrl+N — primary new action
            if (meta && e.key.toLowerCase() === "n") {
                if (pageAction?.run) {
                    e.preventDefault();
                    pageAction.run();
                }
                return;
            }
            if (isTyping) return;

            // "?" cheatsheet
            if (e.key === "?" && !meta) {
                e.preventDefault();
                setShortcutsOpen((o) => !o);
                return;
            }
            // "/" focus filter
            if (e.key === "/" && !meta) {
                if (filterFocusRef.current) {
                    e.preventDefault();
                    filterFocusRef.current();
                }
                return;
            }
            // G sequences: G then H/F/A/I/C
            if (e.key.toLowerCase() === "g" && !meta) {
                gPressed.current = true;
                clearTimeout(gTimer.current);
                gTimer.current = setTimeout(() => { gPressed.current = false; }, 1200);
                return;
            }
            if (gPressed.current && !meta) {
                const k = e.key.toLowerCase();
                gPressed.current = false;
                clearTimeout(gTimer.current);
                if (k === "h") { e.preventDefault(); navigate("/"); }
                else if (k === "f") { e.preventDefault(); navigate("/master-finance"); }
                else if (k === "a") { e.preventDefault(); navigate("/m/ap"); }
                else if (k === "i") { e.preventDefault(); navigate("/m/ic"); }
                else if (k === "c") { e.preventDefault(); navigate("/m/cn"); }
                return;
            }
            // Escape closes notifications panel (palette closes itself in CommandDialog)
            if (e.key === "Escape") {
                setNotificationsOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [navigate, location.pathname, pageAction, setCommandOpen, setShortcutsOpen, setNotificationsOpen, filterFocusRef]);
}
